import type {
  PanelWithData,
  StatChartData,
  StatDataPoint,
  StatDataQuery,
  StatPanel,
  StatTableData,
} from '../types/statistics';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

export function applyFilters(
  points: StatDataPoint[],
  filters: StatDataQuery['filters'],
): StatDataPoint[] {
  if (!filters) return points;
  return points.filter((p) => {
    if (filters.variable !== undefined && p.variable !== filters.variable) return false;
    if (filters.dimension !== undefined && p.dimension !== filters.dimension) return false;
    if (filters.subdimension !== undefined && p.subdimension !== filters.subdimension) return false;
    if (filters.category !== undefined && p.category !== filters.category) return false;
    if (filters.subcategory1 !== undefined && p.subcategory1 !== filters.subcategory1) return false;
    if (filters.subcategory2 !== undefined && p.subcategory2 !== filters.subcategory2) return false;
    if (filters.subcategory3 !== undefined && p.subcategory3 !== filters.subcategory3) return false;
    if (filters.year !== undefined && p.year !== filters.year) return false;
    return true;
  });
}

function buildInfographic(points: StatDataPoint[]): number | string | undefined {
  if (points.length === 0) return undefined;
  return points.reduce((sum, p) => sum + (p.value ?? 0), 0);
}

function buildFooterText(
  points: StatDataPoint[],
  field: keyof Omit<StatDataPoint, 'id' | 'value'> | undefined,
): string | undefined {
  if (!field || points.length === 0) return undefined;
  const seen = new Set<string>();
  for (const p of points) {
    const raw = p[field];
    if (raw !== undefined && raw !== null && raw !== '') seen.add(String(raw));
  }
  if (seen.size === 0) return undefined;
  return Array.from(seen).join(', ');
}

function buildChart(
  points: StatDataPoint[],
  labelField: StatDataQuery['labelField'],
  excludedLabelValues?: string[],
): StatChartData | undefined {
  if (!labelField || points.length === 0) return undefined;

  const excluded = new Set(excludedLabelValues ?? []);

  // Collect ordered unique labels preserving first-seen order
  const labels: string[] = [];
  const seenLabels = new Set<string>();
  for (const p of points) {
    const raw = p[labelField];
    const label = raw !== undefined && raw !== null ? String(raw) : '';
    if (!seenLabels.has(label) && !excluded.has(label)) {
      seenLabels.add(label);
      labels.push(label);
    }
  }

  // Sum value per label
  const valueMap = new Map<string, number>();
  for (const p of points) {
    const raw = p[labelField];
    const label = raw !== undefined && raw !== null ? String(raw) : '';
    valueMap.set(label, (valueMap.get(label) ?? 0) + (p.value ?? 0));
  }

  return {
    labels,
    datasets: [{ values: labels.map((l) => valueMap.get(l) ?? 0) }],
  };
}

function buildStackedChart(
  points: StatDataPoint[],
  labelField: StatDataQuery['labelField'],
  seriesField: StatDataQuery['seriesField'],
  excludedLabelValues?: string[],
): StatChartData | undefined {
  if (!labelField || !seriesField || points.length === 0) return undefined;

  const excluded = new Set(excludedLabelValues ?? []);

  // Collect ordered unique labels and series values
  const labels: string[] = [];
  const seenLabels = new Set<string>();
  const seriesValues: string[] = [];
  const seenSeries = new Set<string>();

  for (const p of points) {
    const lRaw = p[labelField];
    const label = lRaw !== undefined && lRaw !== null ? String(lRaw) : '';
    if (!seenLabels.has(label) && !excluded.has(label)) { seenLabels.add(label); labels.push(label); }

    const sRaw = p[seriesField];
    const series = sRaw !== undefined && sRaw !== null ? String(sRaw) : '';
    if (!seenSeries.has(series)) { seenSeries.add(series); seriesValues.push(series); }
  }

  // Build a lookup: label → series → sum (only for non-excluded labels)
  const lookup = new Map<string, Map<string, number>>();
  for (const p of points) {
    const lRaw = p[labelField];
    const label = lRaw !== undefined && lRaw !== null ? String(lRaw) : '';
    if (excluded.has(label)) continue;
    const sRaw = p[seriesField];
    const series = sRaw !== undefined && sRaw !== null ? String(sRaw) : '';

    if (!lookup.has(label)) lookup.set(label, new Map());
    const inner = lookup.get(label)!;
    inner.set(series, (inner.get(series) ?? 0) + (p.value ?? 0));
  }

  const datasets = seriesValues.map((s) => ({
    label: s,
    values: labels.map((l) => lookup.get(l)?.get(s) ?? 0),
  }));

  return { labels, datasets };
}

function buildTable(
  points: StatDataPoint[],
  columns: StatDataQuery['columns'],
): StatTableData | undefined {
  if (!columns || columns.length === 0 || points.length === 0) return undefined;

  // Separate grouping columns from the value column.
  // The convention is: columns always includes 'value' as the last entry;
  // all other columns are the GROUP BY keys.
  type GroupKey = keyof Omit<StatDataPoint, 'id' | 'value'>;
  const groupCols = columns.filter((c) => c !== 'value') as GroupKey[];

  if (groupCols.length === 0) {
    // Only 'value' selected — sum everything into a single row
    const total = points.reduce((acc, p) => acc + (p.value ?? 0), 0);
    return { headers: ['value'], rows: [[total]] };
  }

  // Aggregate: GROUP BY groupCols, SUM value
  const keyFor = (p: StatDataPoint) =>
    groupCols.map((c) => String(p[c] ?? '')).join('\x00');

  const insertionOrder: string[] = [];
  const seenKeys = new Set<string>();
  const sumMap = new Map<string, number>();
  const repMap = new Map<string, StatDataPoint>(); // one representative row per group

  for (const p of points) {
    const k = keyFor(p);
    if (!seenKeys.has(k)) {
      seenKeys.add(k);
      insertionOrder.push(k);
      sumMap.set(k, 0);
      repMap.set(k, p);
    }
    sumMap.set(k, (sumMap.get(k) ?? 0) + (p.value ?? 0));
  }

  const headers = [...groupCols.map(String), 'value'];
  const rows = insertionOrder.map((k) => {
    const rep = repMap.get(k)!;
    return [...groupCols.map((c) => rep[c] ?? ''), sumMap.get(k) ?? 0];
  });

  return { headers, rows: rows as (string | number | null)[][] };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pure function — no Firestore, no React, no platform deps.
 * Applies the panel's dataQuery rules to the provided data points and returns
 * a PanelWithData with the computed dataset ready for rendering.
 *
 * @param panel - The StatPanel configuration
 * @param dataPoints - All StatDataPoints for the panel's theme (pre-fetched)
 */
export function computeStatPanel(
  panel: StatPanel,
  dataPoints: StatDataPoint[],
): PanelWithData {
  const { dataQuery, displayType } = panel;
  const filtered = applyFilters(dataPoints, dataQuery.filters);

  const excludedLabels = new Set(dataQuery.excludedLabelValues ?? []);
  const filteredForFooter =
    dataQuery.labelField && excludedLabels.size > 0
      ? filtered.filter((p) => {
          const raw = p[dataQuery.labelField!];
          const label = raw !== undefined && raw !== null ? String(raw) : '';
          return !excludedLabels.has(label);
        })
      : filtered;

  const computedFooter1 = buildFooterText(filteredForFooter, panel.footerField1);
  const computedFooter2 = buildFooterText(filteredForFooter, panel.footerField2);

  const base = { computedFooter1, computedFooter2 };

  if (displayType === 'infographic') {
    return { ...panel, ...base, computedValue: buildInfographic(filtered) };
  }

  if (displayType === 'stacked_bar_chart') {
    return {
      ...panel,
      ...base,
      computedChart: buildStackedChart(filtered, dataQuery.labelField, dataQuery.seriesField, dataQuery.excludedLabelValues),
    };
  }

  if (displayType === 'bar_chart' || displayType === 'pie_chart') {
    return { ...panel, ...base, computedChart: buildChart(filtered, dataQuery.labelField, dataQuery.excludedLabelValues) };
  }

  if (displayType === 'table') {
    return { ...panel, ...base, computedTable: buildTable(filtered, dataQuery.columns) };
  }

  return { ...panel, ...base };
}
